// src/components/resumo/MediaResizeManager.ts
// 🎯 GERENCIADOR DE REDIMENSIONAMENTO DE MÍDIAS
// Adiciona resize handles e permite redimensionar imagens/vídeos inline

export class MediaResizeManager {
    private editorRef: React.RefObject<HTMLDivElement | null>;
    private resizingData: {
        container: HTMLElement;
        element: HTMLElement;
        handle: string;
        startX: number;
        startY: number;
        startWidth: number;
        startHeight: number;
        aspectRatio: number;
    } | null = null;
    private positionToolbar: HTMLElement | null = null;
    private scrollHandler: (() => void) | null = null;
    private outsideClickHandler: ((e: MouseEvent) => void) | null = null;

    constructor(editorRef: React.RefObject<HTMLDivElement | null>) {
        this.editorRef = editorRef;
    }

    // ========== TOOLBAR DE POSICIONAMENTO ==========
    private getParentBlock(container: HTMLElement): HTMLElement | null {
        let el: HTMLElement | null = container.parentElement;
        while (el) {
            const tag = el.tagName.toLowerCase();
            if (['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'blockquote'].includes(tag)) return el;
            if (el.getAttribute('contenteditable') === 'true') return null;
            el = el.parentElement;
        }
        return null;
    }

    private triggerSave() {
        if (this.editorRef.current) {
            this.editorRef.current.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }

    applyAlignment(container: HTMLElement, align: 'left' | 'center' | 'right') {
        container.style.float = '';
        container.style.display = 'block';
        container.style.margin = '12px 0';
        container.style.marginLeft = align === 'center' ? 'auto' : '0';
        container.style.marginRight = align === 'center' ? 'auto' : '0';
        const parent = this.getParentBlock(container);
        if (parent) parent.style.textAlign = align;
        this.triggerSave();
        this.showPositionToolbar(container);
    }

    applyFloat(container: HTMLElement, direction: 'left' | 'right' | 'none') {
        if (direction === 'left') {
            container.style.float = 'left';
            container.style.display = 'inline-block';
            container.style.margin = '4px 16px 8px 0';
            const parent = this.getParentBlock(container);
            if (parent) parent.style.textAlign = '';
        } else if (direction === 'right') {
            container.style.float = 'right';
            container.style.display = 'inline-block';
            container.style.margin = '4px 0 8px 16px';
            const parent = this.getParentBlock(container);
            if (parent) parent.style.textAlign = '';
        } else {
            container.style.float = '';
            container.style.display = 'block';
            container.style.margin = '12px 0';
            container.style.marginLeft = '0';
            container.style.marginRight = '0';
        }
        this.triggerSave();
        this.showPositionToolbar(container);
    }

    private hidePositionToolbar() {
        if (this.positionToolbar) {
            this.positionToolbar.remove();
            this.positionToolbar = null;
        }
        if (this.scrollHandler) {
            window.removeEventListener('scroll', this.scrollHandler, true);
            this.scrollHandler = null;
        }
        if (this.outsideClickHandler) {
            document.removeEventListener('mousedown', this.outsideClickHandler);
            this.outsideClickHandler = null;
        }
    }

    private updateToolbarPosition(container: HTMLElement) {
        if (!this.positionToolbar) return;
        const rect = container.getBoundingClientRect();
        const tw = this.positionToolbar.offsetWidth || 248;
        let top = rect.top - 48;
        if (top < 8) top = rect.bottom + 8;
        let left = rect.left + rect.width / 2 - tw / 2;
        left = Math.max(8, Math.min(left, window.innerWidth - tw - 8));
        this.positionToolbar.style.top = top + 'px';
        this.positionToolbar.style.left = left + 'px';
    }

    showPositionToolbar(container: HTMLElement) {
        this.hidePositionToolbar();

        const toolbar = document.createElement('div');
        toolbar.className = 'media-position-toolbar';
        toolbar.addEventListener('mousedown', e => e.stopPropagation());

        const currentFloat = container.style.float;
        const parent = this.getParentBlock(container);
        const currentAlign = parent?.style.textAlign || 'left';

        const svgAlign = (lines: string) =>
            `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">${lines}</svg>`;

        const icons = {
            left:       svgAlign('<line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="17" y2="18"/>'),
            center:     svgAlign('<line x1="3" y1="6" x2="21" y2="6"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="5" y1="18" x2="19" y2="18"/>'),
            right:      svgAlign('<line x1="3" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="7" y1="18" x2="21" y2="18"/>'),
            floatLeft:  `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="9" height="9" rx="1" fill="currentColor" fill-opacity="0.15"/><line x1="14" y1="6" x2="22" y2="6"/><line x1="14" y1="10" x2="22" y2="10"/><line x1="2" y1="16" x2="22" y2="16"/><line x1="2" y1="20" x2="22" y2="20"/></svg>`,
            floatRight: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="13" y="3" width="9" height="9" rx="1" fill="currentColor" fill-opacity="0.15"/><line x1="2" y1="6" x2="10" y2="6"/><line x1="2" y1="10" x2="10" y2="10"/><line x1="2" y1="16" x2="22" y2="16"/><line x1="2" y1="20" x2="22" y2="20"/></svg>`,
            noFloat:    `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="8" rx="1"/><line x1="2" y1="16" x2="22" y2="16"/><line x1="2" y1="20" x2="22" y2="20"/></svg>`,
            moveUp:      `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>`,
            moveDown:    `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>`,
            editCaption: `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>`,
            delete:      `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path></svg>`,
        };

        type Btn = { action: string; icon: string; title: string; active: boolean; danger?: boolean };
        const items: (Btn | 'divider')[] = [
            { action: 'align-left',   icon: icons.left,       title: 'Alinhar à esquerda', active: !currentFloat && currentAlign !== 'center' && currentAlign !== 'right' },
            { action: 'align-center', icon: icons.center,     title: 'Centralizar',         active: !currentFloat && currentAlign === 'center' },
            { action: 'align-right',  icon: icons.right,      title: 'Alinhar à direita',   active: !currentFloat && currentAlign === 'right' },
            'divider',
            { action: 'float-left',   icon: icons.floatLeft,  title: 'Texto à direita da imagem', active: currentFloat === 'left' },
            { action: 'float-right',  icon: icons.floatRight, title: 'Texto à esquerda da imagem', active: currentFloat === 'right' },
            { action: 'float-none',   icon: icons.noFloat,    title: 'Sem texto ao lado',   active: !currentFloat || currentFloat === 'none' },
            'divider',
            { action: 'move-up',      icon: icons.moveUp,      title: 'Mover para cima',     active: false },
            { action: 'move-down',    icon: icons.moveDown,    title: 'Mover para baixo',    active: false },
            'divider',
            { action: 'edit-caption', icon: icons.editCaption, title: 'Editar legenda',       active: false },
            { action: 'delete',       icon: icons.delete,      title: 'Excluir imagem',       active: false, danger: true },
        ];

        items.forEach(item => {
            if (item === 'divider') {
                const sep = document.createElement('div');
                sep.className = 'mpt-divider';
                toolbar.appendChild(sep);
                return;
            }
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.innerHTML = item.icon;
            btn.title = item.title;
            btn.className = 'mpt-btn' + (item.active ? ' active' : '');
            if (item.danger) btn.style.color = '#dc2626';
            btn.addEventListener('click', e => {
                e.preventDefault();
                e.stopPropagation();
                if (item.action === 'align-left')   this.applyAlignment(container, 'left');
                if (item.action === 'align-center')  this.applyAlignment(container, 'center');
                if (item.action === 'align-right')   this.applyAlignment(container, 'right');
                if (item.action === 'float-left')    this.applyFloat(container, 'left');
                if (item.action === 'float-right')   this.applyFloat(container, 'right');
                if (item.action === 'float-none')    this.applyFloat(container, 'none');
                if (item.action === 'move-up')       this.moveMedia(container, 'up');
                if (item.action === 'move-down')     this.moveMedia(container, 'down');
                if (item.action === 'edit-caption')  this.editCaption(container);
                if (item.action === 'delete')        this.deleteMedia(container);
            });
            toolbar.appendChild(btn);
        });

        document.body.appendChild(toolbar);
        this.positionToolbar = toolbar;
        this.updateToolbarPosition(container);

        this.scrollHandler = () => this.updateToolbarPosition(container);
        window.addEventListener('scroll', this.scrollHandler, true);

        // Fechar ao clicar fora da imagem e da toolbar
        setTimeout(() => {
            this.outsideClickHandler = (e: MouseEvent) => {
                const target = e.target as Node;
                if (!container.contains(target) && !toolbar.contains(target)) {
                    this.hidePositionToolbar();
                    container.classList.remove('selected');
                }
            };
            document.addEventListener('mousedown', this.outsideClickHandler);
        }, 0);
    }

    // ========== INICIALIZAR TODAS AS MÍDIAS ==========
    initializeAllMedia() {
        const editor = this.editorRef.current;
        if (!editor) return;

        const mediaContainers = editor.querySelectorAll('.media-container');
        console.log(`🖼️ Inicializando ${mediaContainers.length} mídias com resize...`);

        mediaContainers.forEach(container => {
            this.initializeMediaContainer(container as HTMLElement);
        });

        console.log(`✅ ${mediaContainers.length} mídias inicializadas`);
    }

    // ========== INICIALIZAR CONTAINER INDIVIDUAL ==========
    private initializeMediaContainer(container: HTMLElement) {
        // Remove max-width do elemento de mídia para não travar resize em páginas antigas
        const mediaEl = container.querySelector('img, video, iframe') as HTMLElement | null;
        if (mediaEl) mediaEl.style.setProperty('max-width', 'none', 'important');

        // ✅ Remover handles antigos
        const oldHandles = container.querySelectorAll('.resize-handle');
        oldHandles.forEach(handle => handle.remove());

        // ✅ Adicionar resize handles
        const handlePositions = ['nw', 'ne', 'sw', 'se', 'n', 's', 'w', 'e'];

        handlePositions.forEach(pos => {
            const handle = document.createElement('div');
            handle.className = `resize-handle ${pos}`;
            handle.style.cssText = `
                position: absolute;
                width: 12px;
                height: 12px;
                background: #667eea;
                border: 2px solid white;
                border-radius: 50%;
                z-index: 10;
                opacity: 0;
                transition: opacity 0.2s, transform 0.1s;
                pointer-events: all;
            `;

            // Posicionar handle
            switch (pos) {
                case 'nw':
                    handle.style.top = '-6px';
                    handle.style.left = '-6px';
                    handle.style.cursor = 'nw-resize';
                    break;
                case 'ne':
                    handle.style.top = '-6px';
                    handle.style.right = '-6px';
                    handle.style.cursor = 'ne-resize';
                    break;
                case 'sw':
                    handle.style.bottom = '-6px';
                    handle.style.left = '-6px';
                    handle.style.cursor = 'sw-resize';
                    break;
                case 'se':
                    handle.style.bottom = '-6px';
                    handle.style.right = '-6px';
                    handle.style.cursor = 'se-resize';
                    break;
                case 'n':
                    handle.style.top = '-6px';
                    handle.style.left = '50%';
                    handle.style.transform = 'translateX(-50%)';
                    handle.style.cursor = 'n-resize';
                    break;
                case 's':
                    handle.style.bottom = '-6px';
                    handle.style.left = '50%';
                    handle.style.transform = 'translateX(-50%)';
                    handle.style.cursor = 's-resize';
                    break;
                case 'w':
                    handle.style.left = '-6px';
                    handle.style.top = '50%';
                    handle.style.transform = 'translateY(-50%)';
                    handle.style.cursor = 'w-resize';
                    break;
                case 'e':
                    handle.style.right = '-6px';
                    handle.style.top = '50%';
                    handle.style.transform = 'translateY(-50%)';
                    handle.style.cursor = 'e-resize';
                    break;
            }

            container.appendChild(handle);

            // ✅ Adicionar listener de resize
            handle.addEventListener('mousedown', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.startResize(container, pos, e as MouseEvent);
            });
        });

        // ✅ Tornar container posicionado
        container.style.position = 'relative';
        // Usa display:block por padrão; inline-block só quando há float ativo
        if (container.style.float && container.style.float !== 'none') {
            container.style.display = 'inline-block';
        } else {
            container.style.display = 'block';
        }

        // ✅ Mostrar handles ao hover
        container.addEventListener('mouseenter', () => {
            const handles = container.querySelectorAll('.resize-handle');
            handles.forEach(h => {
                (h as HTMLElement).style.opacity = '1';
            });
        });

        container.addEventListener('mouseleave', () => {
            if (!this.resizingData) {
                const handles = container.querySelectorAll('.resize-handle');
                handles.forEach(h => {
                    (h as HTMLElement).style.opacity = '0';
                });
            }
        });

        // ✅ Selecionar ao clicar
        container.addEventListener('click', (e) => {
            e.stopPropagation();
            this.selectMedia(container);
        });

        // ✅ Bloquear o foco automático do editor ao tocar na imagem (tablet).
        // O container já é contenteditable="false", mas isso não impede o
        // navegador de tratar um toque nele como clique no ANCESTRAL editável
        // (o editorRef raiz) — é um comportamento nativo de WebKit/Blink pra
        // "ilhas" não-editáveis dentro de contenteditable, e é resolvido no
        // touchstart/mousedown, antes do listener de click acima rodar. Em
        // touch isso faz o navegador focar o editor, abrir o teclado virtual
        // e rolar a página pra manter o "cursor" visível — sem nenhum código
        // nosso pedindo esse scroll (daí só acontecer em tablet, nunca com
        // mouse). preventDefault() no mousedown cancela só essa ação padrão
        // (foco/caret) sem suprimir o click sintético que vem depois — mesma
        // técnica já usada nos handles de resize (mousedown abaixo) e no
        // toolbar de posição (showPositionToolbar). Não usa stopPropagation
        // aqui porque não precisamos bloquear nada além do foco.
        //
        // Plano B, se o teste em dispositivo real mostrar que isso não é
        // suficiente: alternar editorRef.current.contentEditable = 'false'
        // no início do toque e voltar pra 'true' logo em seguida (ex: no
        // início do handler de click acima) — torna o ancestral literalmente
        // não-focável durante aquele instante, sem depender de como o
        // navegador resolve a ordem touch→mouse sintético.
        container.addEventListener('mousedown', (e) => {
            e.preventDefault();
        });
    }

    // ========== SELECIONAR MÍDIA ==========
    private selectMedia(container: HTMLElement) {
        const editor = this.editorRef.current;
        if (editor) {
            editor.querySelectorAll('.media-container').forEach(c => {
                if (c !== container) c.classList.remove('selected');
            });
        }
        container.classList.add('selected');
        this.showPositionToolbar(container);
    }

    // ========== INICIAR RESIZE ==========
    private startResize(container: HTMLElement, handle: string, e: MouseEvent) {
        e.stopPropagation();

        const mediaElement = container.querySelector('img, video, iframe') as HTMLElement;
        if (!mediaElement) return;

        const rect = mediaElement.getBoundingClientRect();
        // Usa o width do style inline para evitar valores afetados por max-width do CSS
        const startWidth = parseInt(mediaElement.style.width) || rect.width;
        const startHeight = parseInt(mediaElement.style.height) || rect.height;
        const aspectRatio = startWidth / (startHeight || 1);

        this.resizingData = {
            container,
            element: mediaElement,
            handle,
            startX: e.clientX,
            startY: e.clientY,
            startWidth,
            startHeight,
            aspectRatio
        };

        container.classList.add('resizing');

        document.addEventListener('mousemove', this.handleResize);
        document.addEventListener('mouseup', this.stopResize);

        console.log('🖱️ Iniciando resize de mídia:', { handle });
    }

    // ========== REALIZAR RESIZE ==========
    private handleResize = (e: MouseEvent) => {
        const data = this.resizingData;
        if (!data) return;

        const deltaX = e.clientX - data.startX;
        const deltaY = e.clientY - data.startY;

        let newWidth = data.startWidth;
        let newHeight = data.startHeight;

        // ✅ TIPO WORD: Cantos = proporcional, Laterais = só largura/altura
        switch (data.handle) {
            // 🔵 CANTOS: Mantém proporção (como Word)
            case 'se': // Canto inferior direito
                newWidth = data.startWidth + deltaX;
                newHeight = newWidth / data.aspectRatio;
                break;
            case 'sw': // Canto inferior esquerdo
                newWidth = data.startWidth - deltaX;
                newHeight = newWidth / data.aspectRatio;
                break;
            case 'ne': // Canto superior direito
                newWidth = data.startWidth + deltaX;
                newHeight = newWidth / data.aspectRatio;
                break;
            case 'nw': // Canto superior esquerdo
                newWidth = data.startWidth - deltaX;
                newHeight = newWidth / data.aspectRatio;
                break;

            // 🟢 LATERAIS HORIZONTAIS: Só largura (como Word)
            case 'e': // Direita
                newWidth = data.startWidth + deltaX;
                newHeight = data.startHeight; // ✅ Mantém altura original
                break;
            case 'w': // Esquerda
                newWidth = data.startWidth - deltaX;
                newHeight = data.startHeight; // ✅ Mantém altura original
                break;

            // 🟡 LATERAIS VERTICAIS: Só altura (como Word)
            case 'n': // Cima
                newHeight = data.startHeight - deltaY;
                newWidth = data.startWidth; // ✅ Mantém largura original
                break;
            case 's': // Baixo
                newHeight = data.startHeight + deltaY;
                newWidth = data.startWidth; // ✅ Mantém largura original
                break;
        }

        // Usa o editor inteiro como referência para maxWidth — ignora efeitos de float/inline no pai imediato
        const editorEl = this.editorRef.current;
        const maxWidth = editorEl ? Math.min(1200, editorEl.getBoundingClientRect().width * 0.95) : 1200;

        newWidth = Math.max(100, Math.min(newWidth, maxWidth));
        newHeight = Math.max(50, newHeight); // ✅ Limita altura também

        // Aplicar dimensões ao elemento de mídia e ao container (mantém handles alinhados)
        data.element.style.width = `${newWidth}px`;
        data.element.style.height = `${newHeight}px`;
        data.container.style.width = `${newWidth}px`;
    };

    // ========== FINALIZAR RESIZE ==========
    private stopResize = () => {
        const data = this.resizingData;
        if (!data) return;

        data.container.classList.remove('resizing');

        // Manter selecionado
        setTimeout(() => {
            this.selectMedia(data.container);
        }, 10);

        this.resizingData = null;

        document.removeEventListener('mousemove', this.handleResize);
        document.removeEventListener('mouseup', this.stopResize);

        // Salvar alterações
        if (this.editorRef.current) {
            const inputEvent = new Event('input', { bubbles: true });
            this.editorRef.current.dispatchEvent(inputEvent);
        }

        console.log('✅ Resize finalizado');
    };

    // ========== EDITAR LEGENDA ==========
    private editCaption(container: HTMLElement) {
        let caption = container.querySelector('.media-caption-editable') as HTMLElement | null;

        if (!caption) {
            // Imagens antigas podem não ter o elemento de legenda (foi removido por bug anterior).
            // Cria dinamicamente para permitir adicionar legenda.
            caption = document.createElement('span');
            caption.className = 'media-caption-editable media-caption-empty';
            caption.setAttribute('data-caption', 'true');
            caption.setAttribute('data-caption-text', '');
            caption.setAttribute('contenteditable', 'false');
            caption.setAttribute('title', 'Clique para adicionar legenda');
            caption.style.cssText = 'display: block; font-size: 11px; color: #9ca3af; font-style: italic; margin-top: 4px; text-align: center; cursor: text; padding: 4px 8px; border-radius: 4px; transition: background 0.2s; width: 100%; max-width: 100%; box-sizing: border-box; overflow-wrap: break-word; word-break: break-word; line-height: 1.4;';
            caption.innerHTML = '+ Adicionar legenda<span class="caption-edit-hint" style="margin-left: 4px; font-size: 10px; opacity: 0; transition: opacity 0.2s;">✏</span>';
            container.appendChild(caption);

            const editor = container.closest('[contenteditable="true"]') as HTMLElement;
            if (editor) {
                // Marca como alterado
                editor.dispatchEvent(new Event('input', { bubbles: true }));
                // Pede ao InlineContentManager para re-inicializar captions (síncrono no listener)
                editor.dispatchEvent(new CustomEvent('media-caption-created', { bubbles: false }));
            }
            // Clica após InlineContentManager ter inicializado o handler (síncrono acima)
            setTimeout(() => (caption as HTMLElement).click(), 10);
            return;
        }

        caption.click();
    }

    // ========== EXCLUIR MÍDIA ==========
    private deleteMedia(container: HTMLElement) {
        this.hidePositionToolbar();
        container.remove();
        this.triggerSave();
    }

    // ========== MOVER MÍDIA ==========
    private moveMedia(container: HTMLElement, direction: 'up' | 'down') {
        const editor = this.editorRef.current;
        const parent = container.parentElement;
        if (!parent || !editor) return;

        if (direction === 'up') {
            // Procura o irmão anterior (ignora text nodes vazios)
            let prev: Node | null = container.previousSibling;
            while (prev && prev.nodeType === Node.TEXT_NODE && !(prev.textContent || '').trim()) {
                prev = prev.previousSibling;
            }
            if (prev) {
                parent.insertBefore(container, prev);
            } else if (parent !== editor && parent.parentElement) {
                // Container está dentro de um bloco (ex: <p>), sobe para antes do bloco pai
                parent.parentElement.insertBefore(container, parent);
            }
        } else {
            // Procura o irmão seguinte (ignora text nodes vazios)
            let next: Node | null = container.nextSibling;
            while (next && next.nodeType === Node.TEXT_NODE && !(next.textContent || '').trim()) {
                next = next.nextSibling;
            }
            if (next) {
                const afterNext = next.nextSibling;
                if (afterNext) {
                    parent.insertBefore(container, afterNext);
                } else {
                    parent.appendChild(container);
                }
            } else if (parent !== editor && parent.parentElement) {
                // Container está dentro de um bloco, desce para depois do bloco pai
                const afterParent = parent.nextSibling;
                if (afterParent) {
                    parent.parentElement.insertBefore(container, afterParent);
                } else {
                    parent.parentElement.appendChild(container);
                }
            }
        }

        this.triggerSave();
        container.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setTimeout(() => this.showPositionToolbar(container), 50);
    }

    // ========== CLEANUP ==========
    cleanup() {
        const editor = this.editorRef.current;
        if (!editor) return;

        editor.querySelectorAll('.media-container').forEach(container => {
            container.querySelectorAll('.resize-handle').forEach(h => h.remove());
        });

        document.removeEventListener('mousemove', this.handleResize);
        document.removeEventListener('mouseup', this.stopResize);
        this.hidePositionToolbar();
    }
}